import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { v4 as uuidV4 } from 'uuid';
import { useBoolean } from 'react-use';
import { toast } from 'react-toastify';
import { Form } from '@ui';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { CASE_DEFAULT_VALUES, caseSchema } from '@lp/features/forms/CreateCaseForm/config';
import { CreateCaseForm, FormActions } from '@lp/features/forms/CreateCaseForm';
import { getTxAssigneesDiff } from '@features/components/CaseFormBlocks/utils';
import { CaseFormType, getLateralityPatch } from '@features/forms/CaseForms/schema';
import { useCanCreateKidneyCase } from '@lp/core/hooks/useCanCreateKidneyCase';
import { TMDXFile } from '@/api/apis/files';
import { useAddLink, useCreateClinicalScreeningCaseMutation, useCreateDraftCaseMutation } from '@/queries/case';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useSetNrp } from '@/hooks/useSetNrp';
import { getOPOFormContacts } from '@/hooks/useUpdateOpoContacts';
import { Case, CreateDraftCaseType } from '@/api/apis/case';
import { replaceEmptyWithNull } from '@/utils/helpers';
import { CaseBoardPermissions } from '@/permissions';
import { FileType, useReallocationFilesTransfer } from '@/queries/file';
import { retrieveUnifiedError } from '@/api/utils';

export const CreateCaseModal = () => {
  const { checkPermission } = useContext(AuthContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useBoolean(false);
  const canCreateKidneyCase = useCanCreateKidneyCase();

  const { mutateAsync: createDraftCase } = useCreateDraftCaseMutation();
  const { mutateAsync: createScreeningCase } = useCreateClinicalScreeningCaseMutation();
  const { createNrp } = useSetNrp();
  const { mutateAsync: addLink } = useAddLink();
  const { mutateAsync: transferFiles } = useReallocationFilesTransfer();
  const { uploadFiles } = useUploadFile(FileType.Case);

  const canCreateCases = checkPermission(CaseBoardPermissions.CaseDraftCreate);

  const prepareCaseData = async (formData: CaseFormType) => {
    const {
      donorId,
      donorType,
      serviceType,
      serviceDetails,
      organType,
      laterality,
      donorHospitalId,
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone,
      opoId,
      opoContactId,
      txCenterId,
      recipientHospitalDate,
      recipientHospitalTime,
      recipientHospitalTimeZone,
      opoRequest,
      pediatricCase,
      reAllocationCase,
      reAllocationCaseId,
      files,
      links,
      nrp,
      nrpType,
      hospitalCustomLocation,
      txCenterCustomLocation,
      isExpeditedCase,
      serviceRegionId,
    } = formData;

    try {
      let uploadedFiles: TMDXFile[] = [];

      if (files && files.length > 0) {
        uploadedFiles = await uploadFiles(files);
      }

      const opoContacts = getOPOFormContacts(
        formData.opoContactId,
        formData.secondaryOpoContactId,
        formData.opoSourceContactPhone,
        formData.secondaryOpoContactPhone,
      );

      const defaultTxAssignees = getTxAssigneesDiff({}, formData);

      return {
        nrp,
        nrpType,
        caseId: uuidV4(),
        donorId,
        donorType,
        serviceType,
        serviceDetails,
        organType,
        ...getLateralityPatch(organType, laterality),
        donorHospitalId,
        donorHospitalDate,
        donorHospitalTime,
        donorHospitalTimeZone,
        opoId,
        opoContactId,
        txCenterId,
        recipientHospitalDate,
        recipientHospitalTime,
        recipientHospitalTimeZone:
          recipientHospitalDate && recipientHospitalTime ? recipientHospitalTimeZone : undefined,
        opoRequest,
        pediatricCase,
        reAllocationCase,
        ...(reAllocationCaseId ? { reAllocationCaseId } : {}),
        links,
        files: uploadedFiles,
        hospitalCustomLocation,
        txCenterCustomLocation: txCenterId ? txCenterCustomLocation : undefined,
        opoContactIdIsPrimaryPairs: opoContacts,
        isExpeditedCase,
        defaultTxAssignees,
        isServiceWithoutCoordinator: formData.isServiceWithoutCoordinator ?? false,
        serviceRegionId,
      };
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }

    toast.error('Something went wrong with files saving. Please try again.');

    return null;
  };

  const saveData = async (
    data: CaseFormType,
    mutation: UseMutateAsyncFunction<Case, unknown, CreateDraftCaseType, unknown>,
  ) => {
    const caseData = await prepareCaseData(data);

    if (!caseData) {
      return null;
    }

    const { files, links, nrp, nrpType, ...caseBody } = caseData;

    const caseResult = await mutation({
      ...replaceEmptyWithNull({ ...caseBody }),
      ...(files.length ? { files } : {}),
    });

    const { caseId } = caseResult;

    for await (const { description, url } of links) {
      try {
        await addLink({ caseId, linkData: { description, url } });
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    }

    if (caseData.reAllocationCaseId) await transferFiles({ caseId });

    await createNrp(nrp, nrpType, caseId);

    return caseResult;
  };

  const handleSubmit = async (saveFunction: () => Promise<Case | null>, successMessage: string) => {
    try {
      setIsSubmitting(true);
      const caseResult = await saveFunction();
      if (caseResult) {
        toast.success(successMessage);
        onClose();
      }
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraftSubmit = async (data: CaseFormType) => {
    const saveDraft = () => saveData(data, createDraftCase);
    handleSubmit(saveDraft, 'Draft Case created successfully.');
  };

  const handleSubmitClinicalScreening = async (data: CaseFormType) => {
    const saveClinicalScreening = () => saveData(data, createScreeningCase);
    handleSubmit(saveClinicalScreening, 'Case created successfully.');
  };

  return (
    <>
      <Button
        isDisabled={!canCreateCases}
        size="md"
        variant="solid"
        onClick={onOpen}
        endContent={<MdOutlineAdd size={24} />}
      >
        Create New Case
      </Button>
      <Modal
        classNames={{
          wrapper: 'flex-wrap',
          base: 'p-6 bg-base-80 !m-2 max-h-[95vh]',
          header: '!text-hl font-secondary p-0 pb-4 border-b-1 border-base-30',
          body: 'p-0 my-6',
          footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
        }}
        size="5xl"
        backdrop="opaque"
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
        scrollBehavior="inside"
      >
        <Form<CaseFormType>
          defaultValues={CASE_DEFAULT_VALUES}
          schema={caseSchema}
          onSubmit={handleSubmitClinicalScreening}
        >
          <ModalContent className="scroll">
            {(handleClose) => (
              <>
                <ModalHeader>Create Case</ModalHeader>
                <ModalBody>
                  <CreateCaseForm hasPermission={canCreateCases} canCreateKidneyCase={canCreateKidneyCase} />
                </ModalBody>
                <ModalFooter>
                  <FormActions
                    handleClose={handleClose}
                    isLoading={isSubmitting}
                    handleSubmitDraft={handleDraftSubmit}
                    canCreateKidneyCase={canCreateKidneyCase}
                    isEditMode={false}
                  />
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Form>
      </Modal>
    </>
  );
};
